/**
 * Compact, versioned encoding of a motif and the view settings for share links.
 *
 * Layout (version 1), then base64url; optionally deflate-raw compressed when that is shorter:
 *   u8 version · u8 p · u8 q · u8 r · u8 flags
 *   varint strokeCount
 *   per stroke: u8 header · u8 size · varint n · zigzag-varint (a, b) for the first point and
 *               deltas for the rest (1/4096 domain units) · [n × u8 widths]
 * header: bits 0–2 role + 1 (0 = eraser) · bit 3 rotation symmetry · bit 4 per-point widths
 * flags:  bit 0 rotation group · bits 1–2 colour scheme · bits 3–4 model · bits 5–6 grid
 */
import { classifyTriangle, isValidOrder } from '../geometry/triangle-group';
import { ERASER, MAX_BRUSH, MIN_BRUSH, type Motif, type Stroke, type StrokeRole } from './motif';

export type ColourScheme = 'single' | 'parity' | 'classes';
export type GridMode = 'off' | 'tiles' | 'mirrors';
export type ModelName = 'disk' | 'halfplane' | 'klein' | 'band';

export interface SharedState {
  p: number;
  q: number;
  r: number;
  rotation: boolean;
  scheme: ColourScheme;
  model: ModelName;
  grid: GridMode;
  motif: Motif;
}

const VERSION = 1;
const QUANT = 4096;
const SCHEMES: ColourScheme[] = ['single', 'parity', 'classes'];
const MODELS: ModelName[] = ['disk', 'halfplane', 'klein', 'band'];
const GRIDS: GridMode[] = ['off', 'tiles', 'mirrors'];
const MAX_STROKES = 20000;
const MAX_POINTS = 400000;

export class CodecError extends Error {}

class Writer {
  private buf = new Uint8Array(256);
  private n = 0;
  private ensure(k: number) {
    if (this.n + k <= this.buf.length) return;
    const next = new Uint8Array(Math.max(this.buf.length * 2, this.n + k));
    next.set(this.buf);
    this.buf = next;
  }
  u8(v: number) {
    this.ensure(1);
    this.buf[this.n++] = v & 255;
  }
  varint(v: number) {
    if (!Number.isInteger(v) || v < 0)
      throw new CodecError('varint must be a non-negative integer');
    do {
      let b = v % 128;
      v = Math.floor(v / 128);
      if (v > 0) b |= 128;
      this.u8(b);
    } while (v > 0);
  }
  zigzag(v: number) {
    this.varint(v >= 0 ? 2 * v : -2 * v - 1);
  }
  bytes(): Uint8Array {
    return this.buf.slice(0, this.n);
  }
}

class Reader {
  private i = 0;
  constructor(private readonly buf: Uint8Array) {}
  u8(): number {
    if (this.i >= this.buf.length) throw new CodecError('Unexpected end of data');
    return this.buf[this.i++];
  }
  varint(): number {
    let v = 0;
    let mul = 1;
    for (let k = 0; k < 8; k++) {
      const b = this.u8();
      v += (b & 127) * mul;
      if (!(b & 128)) return v;
      mul *= 128;
    }
    throw new CodecError('Varint too long');
  }
  zigzag(): number {
    const v = this.varint();
    return v % 2 === 0 ? v / 2 : -(v + 1) / 2;
  }
  get done(): boolean {
    return this.i === this.buf.length;
  }
}

export function encodeBinary(state: SharedState): Uint8Array {
  const w = new Writer();
  w.u8(VERSION);
  w.u8(state.p);
  w.u8(state.q);
  w.u8(state.r);
  w.u8(
    (state.rotation ? 1 : 0) |
      (SCHEMES.indexOf(state.scheme) << 1) |
      (MODELS.indexOf(state.model) << 3) |
      (GRIDS.indexOf(state.grid) << 5),
  );
  w.varint(state.motif.strokes.length);
  for (const s of state.motif.strokes) {
    const roleCode = s.role === ERASER ? 0 : s.role + 1;
    const hasWidths = !!s.widths && s.widths.length === s.points.length;
    w.u8(roleCode | (s.symmetry === 'rotation' ? 8 : 0) | (hasWidths ? 16 : 0));
    const t = (s.size - MIN_BRUSH) / (MAX_BRUSH - MIN_BRUSH);
    w.u8(Math.round(Math.max(0, Math.min(1, t)) * 255));
    w.varint(s.points.length);
    let pa = 0;
    let pb = 0;
    for (const p of s.points) {
      const qa = Math.round(p.a * QUANT);
      const qb = Math.round(p.b * QUANT);
      w.zigzag(qa - pa);
      w.zigzag(qb - pb);
      pa = qa;
      pb = qb;
    }
    if (hasWidths) for (const x of s.widths!) w.u8(Math.max(1, Math.round(x * 255)));
  }
  return w.bytes();
}

export function decodeBinary(bytes: Uint8Array): SharedState {
  const r = new Reader(bytes);
  const version = r.u8();
  if (version !== VERSION) throw new CodecError(`Unsupported link version ${version}`);
  const p = r.u8();
  const q = r.u8();
  const rr = r.u8();
  if (![p, q, rr].every(isValidOrder)) throw new CodecError('Triangle orders out of range');
  if (classifyTriangle(p, q, rr).kind !== 'hyperbolic') {
    throw new CodecError('The link describes a triangle that is not hyperbolic');
  }
  const flags = r.u8();
  const scheme = SCHEMES[(flags >> 1) & 3];
  const model = MODELS[(flags >> 3) & 3];
  const grid = GRIDS[(flags >> 5) & 3];
  if (!scheme || !model || !grid) throw new CodecError('Unknown display settings');
  const count = r.varint();
  if (count > MAX_STROKES) throw new CodecError('Too many strokes');
  const strokes: Stroke[] = [];
  let total = 0;
  for (let i = 0; i < count; i++) {
    const header = r.u8();
    const roleCode = header & 7;
    if (roleCode > 4) throw new CodecError('Unknown paint colour');
    const role = (roleCode === 0 ? ERASER : roleCode - 1) as StrokeRole;
    const size = MIN_BRUSH + (r.u8() / 255) * (MAX_BRUSH - MIN_BRUSH);
    const n = r.varint();
    total += n;
    if (n < 1 || total > MAX_POINTS) throw new CodecError('Bad point count');
    const points = new Array(n);
    let pa = 0;
    let pb = 0;
    for (let k = 0; k < n; k++) {
      pa += r.zigzag();
      pb += r.zigzag();
      const a = pa / QUANT;
      const b = pb / QUANT;
      if (Math.abs(a) > 8 || Math.abs(b) > 8) throw new CodecError('Point outside the domain');
      points[k] = { a, b };
    }
    let widths: number[] | undefined;
    if (header & 16) {
      widths = [];
      for (let k = 0; k < n; k++) widths.push(r.u8() / 255);
    }
    strokes.push({ role, symmetry: header & 8 ? 'rotation' : 'reflection', size, points, widths });
  }
  if (!r.done) throw new CodecError('Trailing data');
  return { p, q, r: rr, rotation: (flags & 1) === 1, scheme, model, grid, motif: { strokes } };
}

/* base64url */
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
const LOOKUP = new Int16Array(128).fill(-1);
for (let i = 0; i < ALPHABET.length; i++) LOOKUP[ALPHABET.charCodeAt(i)] = i;

export function toBase64Url(bytes: Uint8Array): string {
  let out = '';
  let i = 0;
  for (; i + 2 < bytes.length; i += 3) {
    const v = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];
    out +=
      ALPHABET[v >> 18] + ALPHABET[(v >> 12) & 63] + ALPHABET[(v >> 6) & 63] + ALPHABET[v & 63];
  }
  const rest = bytes.length - i;
  if (rest === 1) {
    const v = bytes[i] << 16;
    out += ALPHABET[v >> 18] + ALPHABET[(v >> 12) & 63];
  } else if (rest === 2) {
    const v = (bytes[i] << 16) | (bytes[i + 1] << 8);
    out += ALPHABET[v >> 18] + ALPHABET[(v >> 12) & 63] + ALPHABET[(v >> 6) & 63];
  }
  return out;
}

export function fromBase64Url(s: string): Uint8Array {
  if (s.length % 4 === 1) throw new CodecError('Malformed link (length)');
  const out = new Uint8Array(Math.floor((s.length * 3) / 4));
  let o = 0;
  let acc = 0;
  let bits = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    const v = c < 128 ? LOOKUP[c] : -1;
    if (v < 0) throw new CodecError('Malformed link (character)');
    acc = (acc << 6) | v;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out[o++] = (acc >> bits) & 255;
    }
  }
  return out.slice(0, o);
}

async function pipe(bytes: Uint8Array, stream: CompressionStream | DecompressionStream) {
  const blob = new Blob([bytes as BlobPart]);
  const res = new Response(blob.stream().pipeThrough(stream));
  return new Uint8Array(await res.arrayBuffer());
}

const canCompress = () =>
  typeof CompressionStream === 'function' && typeof DecompressionStream === 'function';

/** Encodes to a URL-safe string: 'r…' (raw) or 'z…' (deflate-raw), whichever is shorter. */
export async function encodeState(state: SharedState): Promise<string> {
  const raw = encodeBinary(state);
  const plain = 'r' + toBase64Url(raw);
  if (!canCompress()) return plain;
  try {
    const z = 'z' + toBase64Url(await pipe(raw, new CompressionStream('deflate-raw')));
    return z.length < plain.length ? z : plain;
  } catch {
    return plain;
  }
}

export async function decodeState(s: string): Promise<SharedState> {
  if (s.length < 2) throw new CodecError('Empty link');
  const kind = s[0];
  const body = fromBase64Url(s.slice(1));
  if (kind === 'r') return decodeBinary(body);
  if (kind === 'z') {
    if (!canCompress()) throw new CodecError('This browser cannot read compressed links');
    let raw: Uint8Array;
    try {
      raw = await pipe(body, new DecompressionStream('deflate-raw'));
    } catch {
      throw new CodecError('Malformed link (compression)');
    }
    return decodeBinary(raw);
  }
  throw new CodecError('Unknown link format');
}
